import { Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import type { InterviewFeedback } from "@/client/services/ai/job-match";
import { useInterviewAnswer } from "@/client/services/ai/job-match";

const scoreText = (score: number) =>
  score >= 75 ? "text-success" : score >= 50 ? "text-warning" : "text-error";

type Props = {
  questions: { question: string; focus?: string }[];
  resume: string;
  jobDescription: string;
};

export const InterviewPractice = ({ questions, resume, jobDescription }: Props) => {
  const { scoreAnswer, loading } = useInterviewAnswer();

  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState<InterviewFeedback | null>(null);

  const question = questions[index];

  const go = (delta: number) => {
    setIndex((current) => Math.min(Math.max(current + delta, 0), questions.length - 1));
    setAnswer("");
    setFeedback(null);
  };

  const onScore = async () => {
    if (answer.trim().length < 20) {
      toast({ variant: "warning", title: t`Write a fuller answer before scoring.` });
      return;
    }

    try {
      const result = await scoreAnswer({
        resume,
        jobDescription,
        question: question.question,
        answer,
        locale: i18n.locale,
      });
      setFeedback(result);
    } catch (error) {
      toast({ variant: "error", title: t`Scoring failed`, description: (error as Error).message });
    }
  };

  return (
    <div className="grid gap-y-3">
      <div className="flex items-center justify-between text-xs text-foreground/60">
        <span>{t`Question ${index + 1} of ${questions.length}`}</span>
        <div className="flex items-center gap-x-1">
          <button
            type="button"
            disabled={index === 0}
            className="rounded p-1 hover:bg-secondary disabled:opacity-30"
            onClick={() => {
              go(-1);
            }}
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            disabled={index === questions.length - 1}
            className="rounded p-1 hover:bg-secondary disabled:opacity-30"
            onClick={() => {
              go(1);
            }}
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <p className="text-sm font-medium">{question.question}</p>

      <textarea
        value={answer}
        rows={4}
        placeholder={t`Type your answer (STAR: situation, task, action, result)...`}
        className="w-full resize-y rounded-md border bg-background p-3 text-sm outline-none ring-primary focus:ring-1"
        onChange={(event) => {
          setAnswer(event.target.value);
        }}
      />

      <Button size="sm" disabled={loading} onClick={onScore}>
        {loading ? (
          <Loader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 size-4" />
        )}
        {loading ? t`Scoring...` : t`Score my answer`}
      </Button>

      {feedback && (
        <div className="grid gap-y-3 rounded-md border p-3">
          <div className="flex items-center gap-x-3">
            <div className={cn("text-2xl font-bold tabular-nums", scoreText(feedback.score))}>
              {feedback.score}
            </div>
            <p className="text-xs leading-relaxed">{feedback.feedback}</p>
          </div>

          {feedback.improvements.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-foreground/60">
                {t`How to improve`}
              </p>
              <ul className="ml-4 list-disc space-y-1 text-xs text-foreground/70">
                {feedback.improvements.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer font-semibold text-foreground/70">
              {t`Show a model answer`}
            </summary>
            <p className="mt-1 leading-relaxed text-foreground/70">{feedback.sampleAnswer}</p>
          </details>
        </div>
      )}
    </div>
  );
};
